/* eslint @typescript-eslint/no-explicit-any: 0 */

import { clearAllBodyScrollLocks, enableBodyScroll } from 'body-scroll-lock';
import qs from 'qs';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  Configure,
  connectStateResults,
  SearchBox
} from 'react-instantsearch-dom';
import { useLocation } from 'react-router';

import CloseIcon from '~/assets/icons/Close';
import LoadingSpinner from '~/assets/icons/LoadingSpinner';
import SearchIcon from '~/assets/icons/Search';
import RelatedSearchClickEvent from '~/events/RelatedSearchClickEvent';
import RelatedSearchInteractionEvent from '~/events/RelatedSearchInteractionEvent';
import SearchClearInteractionEvent from '~/events/SearchClearInteractionEvent';
import SearchCloseInteractionEvent, {
  type SearchClosePlacement
} from '~/events/SearchCloseInteractionEvent';
import SearchInputClickEvent from '~/events/SearchInputClickEvent';
import SearchInputInteractionEvent from '~/events/SearchInputInteractionEvent';
import SearchInteractionEvent from '~/events/SearchInteractionEvent';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import type {
  AllResultsType,
  AllSearchResultsType,
  SearchPanelProps,
  SearchPanelResultsProps,
  SearchSuggestionProps
} from '~/legacy/types';
import { bem, getAppRoot } from '~/legacy/utils';
import { getPageViewId } from '~/utils/navigationContext';

import type { ModalCloseReason } from '../../ModalFrame';
import StandardModal from '../../StandardModal';
import IfHits from './IfHits';
import InstantSearch, { Index } from './InstantSearch';
import { getSearchResultsBasePath } from './searchResultsPath';
import SearchPanelHits from './SearchPanelHits';
import SearchPanelRefinedResults from './SearchPanelRefinedResults';
import SearchPanelResultsTypes from './SearchPanelResultsTypes';

const options = {
  clothing: {
    clickAnalytics: true,
    filters: 'type:product AND sub_type:clothing',
    hitsPerPage: 5
  },
  footwear: {
    clickAnalytics: true,
    filters: 'type:product AND sub_type:sneaker',
    hitsPerPage: 5
  },
  news: {
    clickAnalytics: true,
    filters: 'type:article',
    hitsPerPage: 5
  },
  suggestion: {
    clickAnalytics: true,
    hitsPerPage: 7
  }
};

const isCurrentSearchTerm = (suggestion = '', searchTerm = '') =>
  suggestion.trim().toLowerCase() === searchTerm.trim().toLowerCase();

const hasRelatedSuggestions = ({ searchResults, searchTerm }: any) =>
  Boolean(
    searchResults?.hits?.some(
      (hit: any) => !isCurrentSearchTerm(hit.query, searchTerm)
    )
  );

const SearchSuggestion = ({
  hit: { query, objectID },
  onClick,
  position,
  searchTerm
}: SearchSuggestionProps) => {
  const { isBot } = useRootData();
  const user = useUser();

  if (isCurrentSearchTerm(query, searchTerm)) {
    return null;
  }

  const handleRelatedSearchesClick = (e: React.MouseEvent<HTMLLIElement>) => {
    const eventData = {
      isBot,
      related_term: query,
      search_term: searchTerm
    };

    const event = new RelatedSearchClickEvent(eventData);
    event.send();

    new RelatedSearchInteractionEvent({
      gesture: 'click',
      guestId: user.guestId,
      isBot,
      placement: 'header',
      position,
      searchTerm: searchTerm || undefined,
      selectedSearchTerm: query
    }).send();

    onClick(e);
  };

  return (
    <li
      key={objectID}
      onMouseDown={(e) => e.preventDefault()}
      onClick={handleRelatedSearchesClick}
      className="btn btn--tag btn--dark"
    >
      {query}
    </li>
  );
};

const hasSuggestions = hasRelatedSuggestions;

const noSuggestions = (props: any) => !hasRelatedSuggestions(props);

const noResults = ({
  allSearchResults
}: {
  allSearchResults: AllSearchResultsType;
}) => {
  const {
    footwear: { nbHits: footwearHits } = { nbHits: 0 },
    news: { nbHits: newsHits } = { nbHits: 0 },
    clothing: { nbHits: clothingHits } = { nbHits: 0 }
  } = allSearchResults || {};

  return footwearHits === 0 && newsHits === 0 && clothingHits === 0;
};

const anyResults = ({
  allSearchResults
}: {
  allSearchResults: AllSearchResultsType;
  searchResults: any;
}) => {
  const {
    footwear: { nbHits: footwearHits } = { nbHits: 0 },
    news: { nbHits: newsHits } = { nbHits: 0 },
    clothing: { nbHits: clothingHits } = { nbHits: 0 }
  } = allSearchResults || {};

  return footwearHits > 0 || newsHits > 0 || clothingHits > 0;
};

export const AllResults = connectStateResults(
  ({ searchState, searchResults }: AllResultsType) => {
    const results = searchResults && searchResults.nbHits !== 0;

    return results ? null : (
      <div className="my-auto flex flex-col justify-center items-center h-full overflow-auto">
        <p className="m-3 text-base text-grey-hide font-heading font-light">
          No results for “{searchState.query}”
        </p>
      </div>
    );
  }
);

const RESULT_SECTIONS = ['footwear', 'news', 'clothing'] as const;

const ResultCount = connectStateResults(
  ({
    allSearchResults,
    onChange,
    searchState,
    searching
  }: {
    allSearchResults: any;
    onChange: (count: number | undefined) => void;
    searchResults: any;
    searchState: any;
    searching?: boolean;
  }) => {
    const query = searchState?.query ?? '';
    const sections = RESULT_SECTIONS.map(
      (section) => allSearchResults?.[section]
    );
    const current =
      !searching &&
      sections.every((results) => results && (results.query ?? '') === query);
    const total = current
      ? sections.reduce((sum, results) => sum + (results.nbHits ?? 0), 0)
      : undefined;

    useEffect(() => {
      onChange(total);
    }, [onChange, total]);

    return null;
  }
);

const DesktopResultsShell = connectStateResults(
  ({
    children,
    closing,
    hasQuery,
    searching
  }: {
    children: ReactNode;
    closing?: boolean;
    hasQuery: boolean;
    searching?: boolean;
  }) => {
    const isLoading = hasQuery && Boolean(searching);

    return (
      <div
        className={bem('search-panel__desktop', {
          closing,
          loading: isLoading
        })}
      >
        <div className="search-panel__desktop-content">{children}</div>
        <div className="search-panel__desktop-spinner" aria-hidden={!isLoading}>
          <LoadingSpinner />
        </div>
      </div>
    );
  }
);

export const SearchPanelResults = ({
  close = () => {},
  closing,
  onResultCount = () => {},
  setQuery = (query: any) => {},
  query,
  displayState,
  setDisplayState,
  isLargeBreakpoint,
  isMediumBreakpoint
}: SearchPanelResultsProps) => {
  const searchLocationFootwear = {
    pathname: '/release-dates/',
    search: qs.stringify({ q: query })
  };
  const searchLocationNews = {
    pathname: '/news/',
    search: qs.stringify({ q: query })
  };

  const searchLocationClothing = {
    pathname: '/clothing/',
    search: qs.stringify({ q: query })
  };

  const redirectLinks = {
    clothing: searchLocationClothing,
    footwear: searchLocationFootwear,
    news: searchLocationNews
  };

  const content = (
    <div className="content-width w-full h-full my-auto flex pb-0 flex-col gap-0 lg:gap-4 lg:pt-6 lg:pb-10">
      <ResultCount onChange={onResultCount} />
      <Index indexName="search_suggestions">
        <Configure {...options.suggestion} />
        <IfHits condition={hasSuggestions} searchTerm={query}>
          <ul className="search-panel__suggestions -mx-4 px-4 no-scrollbar">
            <span className="search-panel__suggestions-message">
              Related searches
            </span>
            <SearchPanelHits
              hitComponent={SearchSuggestion}
              hitProps={{
                onClick: (e: any) => {
                  setQuery(e.target.innerText || e.target.textContent);
                },
                searchTerm: query
              }}
            />
          </ul>
        </IfHits>
        <IfHits condition={noSuggestions} searchTerm={query}>
          <div className="search-panel__suggestions-spacer" />
        </IfHits>
      </Index>

      {!isMediumBreakpoint && !isLargeBreakpoint && (
        <ul className="search-panel__refine">
          <SearchPanelRefinedResults
            displayState={displayState}
            setDisplayState={setDisplayState}
            searchCondition={anyResults}
            redirectLinks={redirectLinks}
            searchOptions={options}
          />
        </ul>
      )}

      <IfHits condition={noResults}>
        <AllResults />
      </IfHits>

      <div className="search-panel__results w-full">
        <SearchPanelResultsTypes
          redirectLinks={redirectLinks}
          searchOptions={options}
          isLargeBreakpoint={isLargeBreakpoint}
          isMediumBreakpoint={isMediumBreakpoint}
          displayState={displayState}
          close={close}
          query={query}
          searchCondition={anyResults}
        />
      </div>
    </div>
  );

  return isLargeBreakpoint ? (
    <DesktopResultsShell closing={closing} hasQuery={Boolean(query)}>
      {content}
    </DesktopResultsShell>
  ) : (
    content
  );
};

const CLOSE_MS = 300;

const dismissalGesture = (placement: SearchClosePlacement) => {
  switch (placement) {
    case 'escape_key':
      return 'keypress';
    case 'search_box':
      // A form submit doesn't say whether Enter or the search icon fired it.
      return undefined;
    default:
      return 'click';
  }
};

// Module-level because the header mounts two panels on mobile (the CSS-hidden desktop one and the
// modal) sharing one `active`, and either may become the visible one when the viewport crosses lg.
// One activation must send one search_input.
let activationReported = false;

const getSearchPanelModifiers = (isLarge: boolean, isActive: boolean) => [
  isLarge ? 'large' : 'small',
  'primary',
  isLarge && isActive ? 'large-active' : '',
  !isLarge ? 'increase-input-size' : ''
];

const SearchPanel = ({
  searchPanelState,
  isLargeBreakpoint,
  isMediumBreakpoint,
  defaultToLargeLayout = false
}: SearchPanelProps) => {
  const { isBot } = useRootData();
  const user = useUser();
  const scrollTargetElement = getAppRoot();
  const [active, setActive] = searchPanelState;
  const [searchState, setSearchState] = useState({});
  const [displayState, setDisplayState] = useState('footwear');
  const [modifiers, setModifiers] = useState<string[]>(() =>
    getSearchPanelModifiers(isLargeBreakpoint || defaultToLargeLayout, active)
  );
  const [closing, setClosing] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>();
  const closeTimeout = useRef<ReturnType<typeof setTimeout>>();
  const panelRef = useRef<HTMLDivElement>(null);
  // Left unset by a search or a result click: those exits are recorded as their own events, so
  // they send no search_close.
  const closeReason = useRef<SearchClosePlacement>();
  // A dismissal is sent only while the page is still this view. A backdrop click on a link
  // navigates before the closing animation ends, and Back / Forward moves the view without
  // `active` changing; either would send a dismissal with no matching search_input.
  const activationViewId = useRef<string>();
  // Captured when the dismissal starts: a response landing during the closing animation would
  // otherwise report results the shopper never saw.
  const closeResultCount = useRef<number>();
  const resultCount = useRef<number>();
  const onResultCount = useCallback((count: number | undefined) => {
    resultCount.current = count;
  }, []);

  const setQuery = (query: any) => {
    setSearchState({ ...searchState, query });
  };

  const { query } = searchState as any;

  const { pathname } = useLocation();
  const searchResultsPath = getSearchResultsBasePath(pathname);

  const finaliseClose = () => {
    closeTimeout.current = undefined;
    setActive(false);
    setClosing(false);
    setModifiers(
      getSearchPanelModifiers(isLargeBreakpoint || defaultToLargeLayout, false)
    );
    clearAllBodyScrollLocks();
    scrollTargetElement && enableBodyScroll(scrollTargetElement);

    const reason = closeReason.current;
    const dismissedWith = closeResultCount.current;
    closeReason.current = undefined;
    closeResultCount.current = undefined;

    // Sent here, not when the close starts: clicking back into the box during the animation
    // cancels the close, and that is not a dismissal.
    if (reason && activationViewId.current === getPageViewId()) {
      new SearchCloseInteractionEvent({
        gesture: dismissalGesture(reason),
        guestId: user.guestId,
        isBot,
        placement: reason,
        resultCount: dismissedWith,
        searchTerm: query || undefined
      }).send();
    }
  };

  const closePanel = (reason?: SearchClosePlacement) => {
    if (closeTimeout.current) {
      // A search or result click during a dismissal's animation replaces the dismissal.
      if (!reason) closeReason.current = undefined;

      return;
    }

    closeReason.current = reason;
    closeResultCount.current = reason ? resultCount.current : undefined;
    setClosing(true);

    if (isLargeBreakpoint) {
      setModifiers(['large', 'primary', '', '']);
    }

    closeTimeout.current = setTimeout(finaliseClose, CLOSE_MS);
  };

  const searchPanelClosed = () => closePanel();

  const searchPanelDismissed = (reason: SearchClosePlacement) =>
    closePanel(reason);

  const cancelPendingClose = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = undefined;
    }

    closeReason.current = undefined;
    closeResultCount.current = undefined;
    setClosing(false);
  };

  const handleSearchInteraction = () => {
    cancelPendingClose();
    setActive(true);
    new SearchInputClickEvent({
      isBot
    }).send();
  };

  const searchBoxClicked = () => {
    cancelPendingClose();
    setActive(true);
    setModifiers(
      getSearchPanelModifiers(isLargeBreakpoint || defaultToLargeLayout, true)
    );
  };

  const resultsProps = {
    close: searchPanelClosed,
    closing,
    displayState,
    onResultCount,
    query,
    setDisplayState,
    setQuery
  };

  // Only the instance on screen for the current breakpoint reports; see activationReported.
  const isVisiblePanel = isLargeBreakpoint === defaultToLargeLayout;

  useEffect(() => {
    if (!active) {
      activationReported = false;
      return;
    }

    if (!isVisiblePanel || activationReported) {
      return;
    }

    activationReported = true;
    activationViewId.current = getPageViewId();

    new SearchInputInteractionEvent({
      gesture: 'focus',
      guestId: user.guestId,
      isBot,
      placement: 'header',
      searchTerm: query || undefined
    }).send();
  }, [active, isBot, isVisiblePanel, query, user.guestId]);

  useEffect(() => {
    return () => {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }

      clearAllBodyScrollLocks();
      scrollTargetElement && enableBodyScroll(scrollTargetElement);
    };
  }, [scrollTargetElement]);

  useEffect(() => {
    if (!active || isLargeBreakpoint) {
      return;
    }

    const animatedContainer = panelRef.current?.closest('.modal-frame__inner');
    const resultsContainer = panelRef.current?.querySelector(
      '.search-panel__results'
    );

    if (!(resultsContainer instanceof HTMLElement)) {
      return;
    }

    resultsContainer.scrollTop = 0;

    const resetScroll = () => {
      resultsContainer.scrollTop = 0;
    };

    animatedContainer?.addEventListener('animationend', resetScroll);

    return () =>
      animatedContainer?.removeEventListener('animationend', resetScroll);
  }, [active, isLargeBreakpoint]);

  useEffect(() => {
    setModifiers(
      getSearchPanelModifiers(isLargeBreakpoint || defaultToLargeLayout, active)
    );
  }, [isLargeBreakpoint, active, defaultToLargeLayout]);

  return (
    <div
      ref={panelRef}
      className={bem('search-panel', [...modifiers, closing ? 'closing' : ''])}
    >
      <InstantSearch
        searchState={searchState}
        onSearchStateChange={setSearchState}
        indexName="search_suggestions"
      >
        <div className={bem('search-panel__input', { active })}>
          <div className="search-panel__input-container content-width flex gap-2 pr-2 lg:px-0 lg:pr-4">
            <div onClick={searchBoxClicked} style={{ flex: 1 }}>
              <a
                href={`${searchResultsPath}?q=${encodeURIComponent(query)}`}
                ref={linkRef as any}
                style={{ display: 'none' }}
              >
                Redirect to search query
              </a>
              <SearchBox
                autoFocus={active}
                className="search-panel__searchbox"
                translations={{ placeholder: 'Search' }}
                onFocus={handleSearchInteraction}
                reset={<CloseIcon width="10" height="10" stroke="#B7B7B8" />}
                submit={<SearchIcon width={16} height={16} stroke="#B7B7B8" />}
                onReset={() => {
                  // This closure still holds the term being cleared.
                  if (!query) return;

                  new SearchClearInteractionEvent({
                    gesture: 'click',
                    guestId: user.guestId,
                    isBot,
                    placement: 'header',
                    searchTerm: query
                  }).send();
                }}
                onSubmit={(e) => {
                  e.preventDefault();

                  const navigated = Boolean(linkRef.current && query);

                  if (navigated) {
                    linkRef.current?.click();
                  }

                  if (query) {
                    new SearchInteractionEvent({
                      destinationPath: navigated
                        ? `${searchResultsPath}?q=${encodeURIComponent(query)}`
                        : undefined,
                      gesture: 'click',
                      guestId: user.guestId,
                      isBot,
                      placement: 'search_box',
                      searchTerm: query
                    }).send();

                    searchPanelClosed();
                  } else {
                    // An empty submit only closes the panel, so it counts as a dismissal.
                    searchPanelDismissed('search_box');
                  }
                }}
              />
            </div>
            <div className="search-panel__close-modal lg:hidden">
              <button
                type="button"
                aria-label="Close search"
                onClick={() => searchPanelDismissed('close_button')}
                className="search-panel__close-modal-icon flex size-6 items-center justify-center"
              >
                <CloseIcon width="12" height="12" stroke="black" />
              </button>
            </div>
          </div>
        </div>

        {active && (
          <>
            {isLargeBreakpoint ? (
              <StandardModal
                onCloseClick={(_event: unknown, reason: ModalCloseReason) =>
                  searchPanelDismissed(reason)
                }
                isFullWidth
                preventBodyScrollLock={false}
                disableClickOutside={false}
                hideClose={false}
                isFullBody={false}
              >
                <SearchPanelResults
                  {...resultsProps}
                  isLargeBreakpoint={isLargeBreakpoint}
                  isMediumBreakpoint={isMediumBreakpoint}
                />
              </StandardModal>
            ) : (
              <SearchPanelResults
                {...resultsProps}
                isLargeBreakpoint={isLargeBreakpoint}
                isMediumBreakpoint={isMediumBreakpoint}
              />
            )}
          </>
        )}
      </InstantSearch>
    </div>
  );
};

export default SearchPanel;
